import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

const languages = [
    { code: 'en', flag: '🇬🇧', label: 'English' },
    { code: 'es', flag: '🇪🇸', label: 'Español' },
    { code: 'pt', flag: '🇵🇹', label: 'Português' },
];

interface LanguageSwitcherProps {
    mobile?: boolean;
    isSticky?: boolean;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ mobile = false, isSticky = false }) => {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const changeLanguage = (code: string) => {
        i18n.changeLanguage(code);
        localStorage.setItem('language', code);
        setOpen(false);
    };

    // Get active language
    const active = languages.find(l => l.code === i18n.language) || languages[0];

    // Mobile: show flags directly (no label/icon)
    if (mobile) {
        return (
            <li style={{
                listStyle: 'none',
                padding: '20px 30px 24px',
                borderTop: '1px solid rgba(255,255,255,0.1)',
                marginTop: '4px',
            }}>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    {languages.map((lang) => {
                        const isActive = i18n.language === lang.code;
                        return (
                            <button
                                key={lang.code}
                                onClick={() => changeLanguage(lang.code)}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px',
                                    minWidth: '70px',
                                    padding: '12px 10px',
                                    background: isActive ? '#d89d37' : 'rgba(255,255,255,0.1)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                }}
                                onTouchStart={(e) => {
                                    if (!isActive) {
                                        e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                                    }
                                }}
                                onTouchEnd={(e) => {
                                    if (!isActive) {
                                        setTimeout(() => {
                                            e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                                        }, 200);
                                    }
                                }}
                            >
                                <span style={{ fontSize: '28px', lineHeight: 1 }}>{lang.flag}</span>
                                <span style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: isActive ? '#ffffff' : 'rgba(255,255,255,0.8)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}>
                                    {lang.code}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </li>
        );
    }

    // Desktop: globe icon + language word → dropdown with flags
    return (
        <div ref={ref} style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle' }}>
            {/* Trigger: globe icon + full language word */}
            <button
                onClick={() => setOpen(!open)}
                aria-label="Change language"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'transparent',
                    border: `1px solid ${isSticky ? 'rgba(44,61,79,0.3)' : 'rgba(255,255,255,0.25)'}`,
                    borderRadius: '4px',
                    padding: '8px 14px',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s, background 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#d89d37'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = isSticky ? 'rgba(44,61,79,0.3)' : 'rgba(255,255,255,0.25)'; }}
            >
                <Globe size={18} color="#d89d37" strokeWidth={2.5} />
                <span style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: isSticky ? '#2c3d4f' : '#ffffff',
                    letterSpacing: '0.3px',
                }}>
                    {active.label}
                </span>
            </button>

            {/* Dropdown with flags */}
            {open && (
                <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    background: '#ffffff',
                    borderRadius: '6px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    overflow: 'hidden',
                    zIndex: 200,
                    minWidth: '160px',
                }}>
                    {languages.map((lang) => {
                        const isActive = i18n.language === lang.code;
                        return (
                            <button
                                key={lang.code}
                                onClick={() => changeLanguage(lang.code)}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '12px 16px',
                                    background: isActive ? '#d89d37' : 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    fontSize: '14px',
                                    color: isActive ? '#ffffff' : '#2c3d4f',
                                    fontWeight: isActive ? '600' : '400',
                                    transition: 'background 0.15s, color 0.15s',
                                }}
                                onMouseEnter={(e) => {
                                    if (!isActive) {
                                        e.currentTarget.style.background = '#f5f0e8';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = isActive ? '#d89d37' : 'transparent';
                                }}
                            >
                                <span style={{ fontSize: '22px', lineHeight: 1 }}>{lang.flag}</span>
                                <span>{lang.label}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default LanguageSwitcher;
